import { Suspense } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import RouteErrorBoundary from '@/router/RouteErrorBoundary';
import { LoginPage, NotFoundPage, protectedRouteItems } from '@/router/routes';
import { selectIsAuthenticated } from '@/store/authSlice';
import ProtectedRoute from '@/router/ProtectedRoute';
import PublicOnlyRoute from '@/router/PublicOnlyRoute';
import PageLoading from '@/components/PageLoading';
import AppShell from '@/layouts/AppShell';

const renderLazyElement = (Component: LazyExoticComponent<ComponentType>) => (
  <Suspense fallback={<PageLoading />}>
    <Component />
  </Suspense>
);

const RootRedirect = () => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  return <Navigate replace to={isAuthenticated ? '/home' : '/login'} />;
};

const protectedChildren: RouteObject[] = protectedRouteItems.map((route) =>
  route.index
    ? {
        index: true,
        element: renderLazyElement(route.component),
      }
    : {
        path: route.routePath,
        element: renderLazyElement(route.component),
      },
);

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootRedirect />,
  },
  {
    path: '/login',
    element: <PublicOnlyRoute>{renderLazyElement(LoginPage)}</PublicOnlyRoute>,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/home',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: protectedChildren,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '*',
    element: renderLazyElement(NotFoundPage),
  },
]);

const AppRouter = () => <RouterProvider router={router} />;

export default AppRouter;
